import React, { useEffect, useState } from 'react';
import {
    DataGrid, GridToolbarContainer,
    GridToolbarExport,
    gridClasses,
} from '@mui/x-data-grid';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import { useParams, useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import { GetUnpaidItems } from '../../REST/company';
import { AddBalance, UpdateInvoiceItems, BulkPayInvoices } from '../../REST/company';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import { getAmount } from '../../helpers/amount';
import { dateFormat } from '../../helpers/dates';
import PaymentIcon from '@mui/icons-material/Payment';
import { PayCardStripe, PayAchStripe } from '../WaitList/stripePayment';


const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

function CustomToolbar() {
    return (
        <GridToolbarContainer className={gridClasses.toolbarContainer}>
            <GridToolbarExport />
        </GridToolbarContainer>
    );
}

export default function Balances({ availAmount, onRefresh }) {
    const [open, setOpen] = useState(false);
    const { id } = useParams()
    const [rows, setRows] = useState([])
    const [data, setData] = useState({})
    const [selectedAmount, setSelectedAmount] = useState([])
    const [page, setPage] = useState(10)
    const [alert, setAlert] = useState(false)
    const [alertMsg, setAlertMsg] = useState('')
    const navigate = useNavigate()

    // Form states for bulk payments
    const [payMethod, setPayMethod] = useState('Prepaid Balance')
    const [payerName, setPayerName] = useState('')
    const [payerEmail, setPayerEmail] = useState('')
    const [payerPhone, setPayerPhone] = useState('')
    const [chequeNum, setChequeNum] = useState('')
    const [notes, setNotes] = useState('')
    const [customAmount, setCustomAmount] = useState(0)

    const [refresh, setRefresh] = useState(false)

    const toggleRefresh = () => setRefresh(refresh => !refresh)

    const amountParser = (params) => getAmount(params.value)
    const dateParser = (params) => dateFormat(params.value)

    useEffect(() => {
        toggleRefresh()
    }, [])

    const handleClickOpen = () => {
        setOpen(true);
        setPayMethod('Prepaid Balance')
        setPayerName('')
        setPayerEmail('')
        setPayerPhone('')
        setChequeNum('')
        setNotes('')
        setCustomAmount(0)
        setSelectedAmount([])
        setAlert(false)
        setAlertMsg('')
    };

    const handleClose = () => {
        setOpen(false);
    };

    const getSelectedTotal = () => {
        if (!Array.isArray(selectedAmount)) return 0;
        return selectedAmount.reduce((acc, each) => {
            const item = data[each];
            if (item) return acc + (item.charges * item.amount);
            return acc;
        }, 0);
    }

    // Automatically sync payment amount when selection changes
    useEffect(() => {
        setCustomAmount(getSelectedTotal())
    }, [selectedAmount, data])

    const overpaymentAllowed = payMethod !== 'Prepaid Balance';
    const amountValid = overpaymentAllowed ? (customAmount > 0) : (customAmount > 0 && customAmount <= getSelectedTotal() + 0.001);

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5, hide: true },
        {
            field: 'name',
            headerName: 'Name',
            flex: 0.5
        },
        {
            field: 'charges',
            headerName: 'Charges',
            flex: 0.5,
            type: 'number',
            renderCell: amountParser
        },
        {
            field: 'amount',
            headerName: 'Quantity',
            flex: 0.5,
            type: 'number'
        },
        {
            field: 'invoiceNumber',
            headerName: 'Invoice Number',
            flex: 0.5
        },
        {
            field: 'dueDate',
            headerName: 'Due Date',
            flex: 0.5,
            type: 'date',
            renderCell: dateParser
        }
    ];

    useEffect(() => {
        (async () => {
            const result = await GetUnpaidItems(id)
            let data = {}
            setRows(result.data.reduce((accum, invoice) => {
                const { lineItems, _id, invoiceNumber, dueDate } = invoice
                return accum.concat(lineItems.map(each => {
                    const val = { ...each, id: each._id, invoiceId: _id, invoiceNumber: invoiceNumber, dueDate }
                    data[each._id] = val
                    return val
                }))
            }, []))
            setData(data)
        })()

    }, [id, refresh])

    useEffect(() => {
        if (alert) {
            const clear = setTimeout(() => {
                setAlert(false)
            }, 5000)
            return function cleanup() {
                clearInterval(clear)
            };
        }
    }, [alert])

    const handleComplete = (receipt) => {
        const payload = {
            tenantId: id,
            method: payMethod,
            amount: customAmount,
            items: selectedAmount.map(each => data[each]),
            payer: {
                name: payerName,
                email: payerEmail,
                phone: payerPhone,
                relation: 'Tenant'
            },
            chequeNum: chequeNum || receipt?.confirmation || '',
            refDetails: notes || receipt?.accountDetails || ''
        }

        BulkPayInvoices(payload).then(() => {
            onRefresh()
            handleClose()
        }).catch(err => {
            setAlertMsg(err.response?.data?.message || 'Bulk payment failed.')
            setAlert(true)
        })
    }

    const handlePay = async () => {
        if (!selectedAmount || selectedAmount.length === 0) {
            setAlertMsg('Please select at least one line item.')
            setAlert(true)
            return
        }

        if (!amountValid) {
            setAlertMsg('Enter a valid amount. Credit card and Prepaid payments cannot exceed the total amount due.')
            setAlert(true)
            return
        }

        if (payMethod === 'Prepaid Balance' && availAmount < customAmount) {
            setAlertMsg('Insufficient Prepaid Balance.')
            setAlert(true)
            return
        }

        if (payMethod !== 'Prepaid Balance' && payMethod !== 'Card' && payMethod !== 'ACH' && (!payerName.trim() || !payerEmail.trim())) {
            setAlertMsg('Payer Name and Email are required.')
            setAlert(true)
            return
        }

        if (payMethod === 'Cheque' && !chequeNum.trim()) {
            setAlertMsg('Cheque Number is required.')
            setAlert(true)
            return
        }

        try {
            const payload = {
                tenantId: id,
                method: payMethod,
                amount: customAmount,
                items: selectedAmount.map(each => data[each]),
                payer: {
                    name: payerName,
                    email: payerEmail,
                    phone: payerPhone
                },
                chequeNum: chequeNum,
                refDetails: notes
            }

            await BulkPayInvoices(payload)
            onRefresh()
            handleClose()
        } catch (err) {
            setAlertMsg(err.response?.data?.message || 'Bulk payment failed.')
            setAlert(true)
        }
    }

    console.log(rows)

    return (
        <div>
            <Button variant="outlined" onClick={handleClickOpen} endIcon={<PaymentIcon />}>
                Pay Invoices
            </Button>
            <Dialog
                fullScreen
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
            >
                <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                            Pay Invoices
                        </Typography>
                    </Toolbar>
                </AppBar>

                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" sx={{ mb: 2 }}>Select Line Items to Pay</Typography>
                            <div style={{ height: 400, width: '100%' }}>
                                <DataGrid
                                    rows={rows}
                                    columns={columns}
                                    pageSize={page}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    onPageSizeChange={e => setPage(e)}
                                    checkboxSelection
                                    disableSelectionOnClick
                                    onSelectionModelChange={(selectionModel, details) => setSelectedAmount(selectionModel)}
                                    components={{
                                        Toolbar: CustomToolbar,
                                    }}
                                />
                            </div>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Paper variant="outlined" sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2 }}>Payment Summary & Method</Typography>
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography color="text.secondary">Tenant Balance:</Typography>
                                    <Typography sx={{ fontWeight: 600 }}>${Number(availAmount).toFixed(2)}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, borderBottom: '1px solid', pb: 1, borderColor: 'divider' }}>
                                    <Typography color="text.secondary">Selected Total Due:</Typography>
                                    <Typography sx={{ fontWeight: 600 }}>${getSelectedTotal().toFixed(2)}</Typography>
                                </Box>

                                {alert && <Alert severity="warning" sx={{ mb: 2 }}>{alertMsg}</Alert>}

                                <TextField
                                    select
                                    fullWidth
                                    label="Payment Method"
                                    size="small"
                                    value={payMethod}
                                    onChange={e => {
                                        setPayMethod(e.target.value)
                                        setAlert(false)
                                    }}
                                    SelectProps={{ native: true }}
                                    sx={{ mb: 2 }}
                                >
                                    <option value="Prepaid Balance">Prepaid Balance</option>
                                    <option value="Card">Credit / Debit Card</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque (Paper)</option>
                                    <option value="ACH">e-Cheque / ACH</option>
                                </TextField>

                                <TextField
                                    label="Amount to Pay"
                                    type="number"
                                    fullWidth
                                    size="small"
                                    value={customAmount}
                                    onChange={e => setCustomAmount(Number(e.target.value))}
                                    sx={{ mb: 2 }}
                                    error={!amountValid}
                                    helperText={
                                        amountValid
                                            ? (overpaymentAllowed && customAmount > getSelectedTotal()
                                                ? `Selected Due: $${getSelectedTotal().toFixed(2)}. Overpayment of $${(customAmount - getSelectedTotal()).toFixed(2)} will be credited to tenant balance.`
                                                : `Selected Due: $${getSelectedTotal().toFixed(2)}`)
                                            : `Enter a valid amount.${!overpaymentAllowed ? ` Amount cannot exceed due: $${getSelectedTotal().toFixed(2)}.` : ''}`
                                    }
                                />

                                {payMethod !== 'Prepaid Balance' && payMethod !== 'Card' && payMethod !== 'ACH' && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>Payer Details</Typography>
                                        <TextField
                                            label="Payer Name"
                                            size="small"
                                            fullWidth
                                            value={payerName}
                                            onChange={e => setPayerName(e.target.value)}
                                        />
                                        <TextField
                                            label="Payer Email"
                                            size="small"
                                            fullWidth
                                            type="email"
                                            value={payerEmail}
                                            onChange={e => setPayerEmail(e.target.value)}
                                        />
                                        <TextField
                                            label="Payer Phone"
                                            size="small"
                                            fullWidth
                                            value={payerPhone}
                                            onChange={e => setPayerPhone(e.target.value)}
                                        />
                                        {payMethod === 'Cheque' && (
                                            <TextField
                                                label="Cheque Number"
                                                size="small"
                                                fullWidth
                                                value={chequeNum}
                                                onChange={e => setChequeNum(e.target.value)}
                                            />
                                        )}
                                        <TextField
                                            label="Notes / Transaction Details"
                                            size="small"
                                            fullWidth
                                            multiline
                                            rows={2}
                                            value={notes}
                                            onChange={e => setNotes(e.target.value)}
                                        />
                                    </Box>
                                )}

                                {selectedAmount.length > 0 && amountValid && payMethod === 'Card' && (
                                    <Box sx={{ mt: 2 }}>
                                        <PayCardStripe 
                                            amount={customAmount} 
                                            invoiceId={selectedAmount.map(each => data[each].invoiceId).join(',')} // Sends all selected invoice IDs
                                            lineItemUids={selectedAmount.map(each => data[each].uid).join(',')} 
                                            onComplete={handleComplete} 
                                        />
                                    </Box>
                                )}

                                {selectedAmount.length > 0 && amountValid && payMethod === 'ACH' && (
                                    <Box sx={{ mt: 2 }}>
                                        <PayAchStripe 
                                            amount={customAmount} 
                                            invoiceId={selectedAmount.map(each => data[each].invoiceId).join(',')} // Sends all selected invoice IDs
                                            lineItemUids={selectedAmount.map(each => data[each].uid).join(',')} 
                                            payerEmail={payerEmail || 'bulk-payer@example.com'} 
                                            onComplete={handleComplete} 
                                        />
                                    </Box>
                                )}

                                {payMethod !== 'Card' && payMethod !== 'ACH' && (
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={handlePay}
                                        sx={{ mt: 3 }}
                                        disabled={selectedAmount.length === 0 || !amountValid || (payMethod === 'Prepaid Balance' && availAmount < customAmount)}
                                    >
                                        Submit Bulk Payment
                                    </Button>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </Dialog>
        </div>
    );
}